import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

export const FORM_SUBMIT_OVERLAY_MODES = {
	PAGE_FORM: 'page-form',
	MODAL_FORM: 'modal-form',
};

/**
 * @typedef {'page-form' | 'modal-form'} FormSubmitOverlayMode
 */

/**
 * @typedef {Object} FormSubmitOverlayProps
 * @property {boolean} open - Whether the loading overlay is visible.
 * @property {FormSubmitOverlayMode} [mode='page-form']
 * Usage intent for overlay behavior:
 * - 'page-form': Full-surface blocking backdrop for page/drawer form submits.
 * - 'modal-form': In-container overlay for modal form submits to avoid z-index stacking issues.
 */

/**
 * Loading overlay shown while a form submission is in-flight.
 *
 * @param {FormSubmitOverlayProps} props
 */
function FormSubmitOverlay({
	open,
	mode = FORM_SUBMIT_OVERLAY_MODES.PAGE_FORM,
}) {
	if (!open) {
		return null;
	}

	if (mode === FORM_SUBMIT_OVERLAY_MODES.MODAL_FORM) {
		return (
			<Box
				role="status"
				aria-label="Submitting form"
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					left: 0,
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					zIndex: 2,
					backgroundColor: 'rgba(255, 255, 255, 0.45)',
				}}
			>
				<CircularProgress color="primary" />
			</Box>
		);
	}

	return (
		<Backdrop
			role="status"
			aria-label="Submitting form"
			sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
			open
		>
			<CircularProgress color="inherit" />
		</Backdrop>
	);
}

FormSubmitOverlay.propTypes = {
	open: PropTypes.bool.isRequired,
	mode: PropTypes.oneOf(Object.values(FORM_SUBMIT_OVERLAY_MODES)),
};

export default FormSubmitOverlay;
